import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { publicApi } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const qc = useQueryClient();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await publicApi.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setInfo(data);
          setState("paid");
          qc.invalidateQueries({ queryKey: ["my-orders"] });
          return;
        }
      } catch (e) { /* keep polling */ }
      attempts.current += 1;
      if (attempts.current >= 15) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, qc]);

  return (
    <div className="max-w-lg mx-auto px-4 py-28 text-center">
      {state === "checking" && (
        <>
          <Loader2 className="w-12 h-12 text-gold mx-auto animate-spin" />
          <h1 className="font-serif text-3xl text-cream mt-6">Confirming your payment…</h1>
          <p className="text-muted2 mt-3">Please wait while we verify with our payment processor.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
          <h1 data-testid="payment-success-heading" className="font-serif text-4xl text-cream mt-6">Payment Confirmed</h1>
          <p className="text-muted2 mt-3">
            Your {info?.payment_type === "balance" ? "balance" : "50% deposit"} has been received. We'll be in touch to finalize your event.
          </p>
          <Link to="/reservations" className="btn-gold mt-8">View My Reservations</Link>
        </>
      )}
      {state === "timeout" && (
        <>
          <Loader2 className="w-12 h-12 text-gold mx-auto" />
          <h1 className="font-serif text-3xl text-cream mt-6">Still processing</h1>
          <p className="text-muted2 mt-3">Your payment is taking a moment. Check My Reservations shortly for the latest status.</p>
          <Link to="/reservations" className="btn-gold mt-8">Go to My Reservations</Link>
        </>
      )}
      {state === "error" && (
        <>
          <XCircle className="w-12 h-12 text-red-400 mx-auto" />
          <h1 className="font-serif text-3xl text-cream mt-6">Something went wrong</h1>
          <p className="text-muted2 mt-3">We couldn't find that payment session.</p>
          <Link to="/reservations" className="btn-gold mt-8">My Reservations</Link>
        </>
      )}
    </div>
  );
}
